import { useState, useMemo, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoJsonLd from '../../components/feature/SeoJsonLd';
import { supabase } from '../../lib/supabase';

const CATEGORIES = ['Tous', 'Épicerie', 'Fruits & Légumes', 'Boulangerie', 'Produits laitiers', 'Boissons', 'Traiteur', 'Bio & Naturel'];

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommandés' },
  { value: 'rating', label: 'Mieux notés' },
  { value: 'distance', label: 'Plus proches' },
  { value: 'new', label: 'Nouveautés' },
];

interface Shop {
  id: string;
  name: string;
  type: string;
  category: string;
  rating: number;
  reviewCount: number;
  deliveryTime: string;
  deliveryFee: string;
  distance: number;
  isNew: boolean;
  discount: number | null;
  image: string;
  tags: string[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const boutiquesPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Boutiques locales – Localygo',
  url: `${SITE_URL}/boutiques`,
  description: 'Découvrez et commandez auprès des boutiques locales de votre quartier : épiceries, boulangeries, fromageries et plus encore. Livraison rapide par Localygo.',
  provider: {
    '@type': 'Organization',
    name: 'Localygo',
    url: SITE_URL,
  },
};

export default function BoutiquesPage() {
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [sortBy, setSortBy] = useState('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShops = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('*')
          .eq('is_active', true)
          .order('rating', { ascending: false });

        if (error) throw error;

        const mapped: Shop[] = (data || []).map((s: any) => {
          const cuisineType = s.cuisine_type || 'Épicerie';
          let category = 'Épicerie';
          
          if (cuisineType.toLowerCase().includes('fruit') || cuisineType.toLowerCase().includes('légume')) {
            category = 'Fruits & Légumes';
          } else if (cuisineType.toLowerCase().includes('boulang') || cuisineType.toLowerCase().includes('pain')) {
            category = 'Boulangerie';
          } else if (cuisineType.toLowerCase().includes('fromage') || cuisineType.toLowerCase().includes('lait')) {
            category = 'Produits laitiers';
          } else if (cuisineType.toLowerCase().includes('vin') || cuisineType.toLowerCase().includes('boisson')) {
            category = 'Boissons';
          } else if (cuisineType.toLowerCase().includes('traiteur')) {
            category = 'Traiteur';
          } else if (cuisineType.toLowerCase().includes('bio') || cuisineType.toLowerCase().includes('naturel')) {
            category = 'Bio & Naturel';
          }

          return {
            id: s.id,
            name: s.name || 'Boutique',
            type: s.description || cuisineType,
            category,
            rating: Number(s.rating) || 4.5,
            reviewCount: s.total_reviews || 0,
            deliveryTime: s.delivery_time || '25-35 min',
            deliveryFee: s.delivery_fee ? `${Number(s.delivery_fee).toFixed(2)} €` : '2,00 €',
            distance: s.latitude && s.longitude ? Math.random() * 3 : 1.5,
            isNew: s.created_at ? new Date(s.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) : false,
            discount: s.is_featured ? 15 : null,
            image: s.image_url || `https://public.readdy.ai/ai/img_res/014df88d4ab79d40bf9f59024e2c1387.jpg$%7Bcategory.toLowerCase%28%29%7D%20shop%20interior%20with%20fresh%20products%20artisan%20goods%20warm%20lighting%20cozy%20atmosphere%20clean%20simple%20background%20professional%20retail%20photography&width=800&height=500&seq=shop-${s.id}&orientation=landscape`,
            tags: [category, 'Local', s.is_verified ? 'Vérifié' : 'Artisan'].filter(Boolean),
          };
        });

        setShops(mapped);
      } catch (err) {
        console.error('Erreur lors du chargement des boutiques:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchShops();

    const channel = supabase
      .channel('shops-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'restaurants' }, () => {
        fetchShops();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    let result = [...shops];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        s.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    if (activeCategory !== 'Tous') {
      result = result.filter(s => s.category === activeCategory);
    }
    switch (sortBy) {
      case 'rating': result.sort((a, b) => b.rating - a.rating); break;
      case 'distance': result.sort((a, b) => a.distance - b.distance); break;
      case 'new': result = result.filter(s => s.isNew).concat(result.filter(s => !s.isNew)); break;
      default: break;
    }
    return result;
  }, [searchQuery, activeCategory, sortBy, shops]);

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={boutiquesPageSchema} id="schema-boutiques" />
      <Navbar />

      {/* Hero */}
      <section className="relative h-80 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=vibrant%20local%20market%20street%20scene%20with%20colorful%20shop%20fronts%20artisan%20products%20fresh%20produce%20warm%20afternoon%20light%20charming%20french%20neighborhood%20boutiques%20and%20small%20businesses%20inviting%20atmosphere%20wide%20angle%20view&width=1440&height=640&seq=boutiques-hero&orientation=landscape"
          alt="Boutiques locales Localygo"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-4 w-full">
          <span className="inline-block bg-teal-500/20 border border-teal-400/40 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
            Commerce local
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            Vos boutiques locales
          </h1>
          <p className="text-white/80 text-base mb-7 max-w-xl mx-auto">
            Soutenez les commerçants de votre quartier. Épiceries, boulangeries, fromageries… livrés chez vous.
          </p>
          <div className="relative max-w-xl mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-lg"></i>
            </div>
            <input
              type="text"
              placeholder="Boutique, produit, catégorie..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Catégories */}
      <section className="sticky top-20 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Barre de tri */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-sm text-gray-500">
            <span className="font-bold text-gray-900">{filtered.length}</span> boutique{filtered.length > 1 ? 's' : ''} trouvée{filtered.length > 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <i className="ri-sort-desc text-gray-400 text-sm"></i>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white cursor-pointer text-gray-700"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Grille */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 text-sm">Chargement des boutiques...</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map(shop => (
              <div key={shop.id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden group cursor-pointer">
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={shop.image}
                    alt={shop.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  {shop.discount && (
                    <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      -{shop.discount}%
                    </span>
                  )}
                  {shop.isNew && (
                    <span className="absolute top-3 right-3 bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      Nouveau
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-0.5 truncate">{shop.name}</h3>
                  <p className="text-xs text-gray-500 mb-2">{shop.type}</p>
                  <div className="flex items-center gap-1 mb-3">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-xs font-semibold text-gray-800">{shop.rating}</span>
                    <span className="text-xs text-gray-400">({shop.reviewCount})</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {shop.tags.map(tag => (
                      <span key={tag} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-50">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i> {shop.deliveryTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-map-pin-line"></i> {shop.distance.toFixed(1)} km
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-truck-line"></i> {shop.deliveryFee}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mb-5">
              <i className="ri-store-2-line text-4xl text-gray-300"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucune boutique trouvée</h3>
            <p className="text-gray-500 text-sm mb-5">Essayez de modifier votre recherche ou la catégorie.</p>
            <button
              onClick={() => { setActiveCategory('Tous'); setSearchQuery(''); }}
              className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Réinitialiser
            </button>
          </div>
        )}
      </section>

      {/* CTA commerçant */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-16 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-5">
            <i className="ri-store-2-line text-3xl"></i>
          </div>
          <h2 className="text-3xl font-extrabold mb-4">Vous êtes commerçant ?</h2>
          <p className="text-teal-100 text-base mb-8 max-w-xl mx-auto">
            Rejoignez Localygo et touchez des milliers de clients dans votre quartier. Inscription gratuite, commission réduite.
          </p>
          <a
            href="/connexion"
            className="inline-block bg-white text-teal-700 font-bold px-8 py-3.5 rounded-full hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Inscrire ma boutique
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}